import { uiText, useUiLanguage } from '@/i18n/interface';
import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Crown, Database, HeadphonesIcon, Settings, Shield, LogOut, Key, CreditCard } from 'lucide-react';
import DojoManagement from '@/components/superadmin/DojoManagement';
import AdminCodeGenerator from '@/components/superadmin/AdminCodeGenerator';
import SupportRequestsTab from '@/components/superadmin/SupportRequestsTab';
import AIAgent from '@/components/superadmin/AIAgent';
import DojoCodesManagement from '@/components/superadmin/DojoCodesManagement';
import SubscriptionManager from '@/components/superadmin/SubscriptionManager';
import Layout from '@/components/Layout';
// Removed unused imports: StripeAccountChecker, StripeSubscriptionChecker, UserChecker

const SuperUserDashboard: React.FC = () => {
  useUiLanguage();
  const { user, profile, isAuthenticated, impersonation, logout } = useAuth();

  console.log('SuperUserDashboard render - Auth state:', {
    isAuthenticated,
    user: user?.email,
    profile: profile ? {
      email: profile.email,
      role: profile.role,
      id: profile.id
    } : null
  });

  // Show loading while authentication state is being determined
  if (!isAuthenticated || !user) {
    console.log('Not authenticated, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  // Show loading while profile is being fetched
  if (!profile) {
    console.log('Profile not loaded yet, showing loading state');
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-400 mx-auto mb-4"></div>
          <p className="text-slate-300 text-lg">{uiText("Loading dashboard...")}</p>
        </div>
      </div>
    );
  }

  const userRole = profile.role as string;
  console.log('Access check - Email:', profile.email, 'Role:', userRole);

  if (profile?.role !== 'super_user') {
    console.log('Access denied - Invalid email or role');
    return <Navigate to="/" replace />;
  }

  console.log('Access granted - Loading SuperUser Dashboard');

  return (
    <Layout title={uiText("System administration")}><div className="wh-superuser min-h-screen bg-background">
      {/* Impersonation Banner - only show in super user dashboard if impersonation is active */}
      {impersonation.active && (
        <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white p-4 text-center border-b border-orange-500/30">
          <div className="flex items-center justify-center gap-2">
            <Shield className="h-5 w-5" />
            <span className="font-semibold">
              {uiText("IMPERSONATION MODE: Currently impersonating")}{' '}{impersonation.dojoName}
            </span>
            <span className="text-sm opacity-90 ml-2">
              {uiText("(Return here to manage impersonation)")}</span>
          </div>
        </div>
      )}

      <div className="wh-container">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6 sm:mb-8">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg sm:rounded-xl blur-sm opacity-75"></div>
              <div className="relative p-3 sm:p-4 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg sm:rounded-xl shadow-lg">
                <Crown className="h-8 w-8 sm:h-10 sm:w-10 text-white drop-shadow-lg" />
              </div>
            </div>
            <div className="flex-1">
              <h1 className="wh-page-title">
                {uiText("Super User Dashboard")}</h1>
              <p className="text-slate-300 text-sm sm:text-lg">{uiText("Global System Control Panel")}</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
            <Badge className="bg-gradient-to-r from-purple-600 to-purple-700 text-white border-purple-500 px-2 sm:px-3 py-1 text-xs sm:text-sm">
              <Shield className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              {uiText("Super User Access")}</Badge>
            <Button
              variant="outline"
              onClick={() => logout()}
              className="flex items-center gap-2 bg-slate-800/50 text-slate-200 border-slate-600 hover:bg-slate-700/50 hover:text-white w-full sm:w-auto justify-center sm:justify-start text-sm"
            >
              <LogOut className="h-4 w-4" />
              {uiText("Logout")}</Button>
          </div>
        </div>

        {/* Main Tabs */}
        <Tabs defaultValue="dojos" className="space-y-4 sm:space-y-6">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-7 bg-slate-800/50 border border-slate-700 h-auto">
            <TabsTrigger
              value="dojos"
              className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-blue-700 data-[state=active]:text-white text-slate-300 p-2 sm:p-3 text-xs sm:text-sm"
            >
              <Database className="h-4 w-4" />
              <span className="hidden sm:inline">{uiText("Dojo Management")}</span>
              <span className="sm:hidden">{uiText("Dojos")}</span>
            </TabsTrigger>
            <TabsTrigger
              value="codes"
              className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-green-600 data-[state=active]:to-green-700 data-[state=active]:text-white text-slate-300 p-2 sm:p-3 text-xs sm:text-sm"
            >
              <Settings className="h-4 w-4" />
              <span className="hidden sm:inline">{uiText("Admin Codes")}</span>
              <span className="sm:hidden">{uiText("Codes")}</span>
            </TabsTrigger>
            <TabsTrigger
              value="dojo-codes"
              className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-600 data-[state=active]:to-indigo-700 data-[state=active]:text-white text-slate-300 p-2 sm:p-3 text-xs sm:text-sm"
            >
              <Key className="h-4 w-4" />
              <span className="hidden sm:inline">{uiText("Dojo Codes")}</span>
              <span className="sm:hidden">{uiText("Join Codes")}</span>
            </TabsTrigger>
            <TabsTrigger
              value="support"
              className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-600 data-[state=active]:to-orange-700 data-[state=active]:text-white text-slate-300 p-2 sm:p-3 text-xs sm:text-sm"
            >
              <HeadphonesIcon className="h-4 w-4" />
              <span className="hidden sm:inline">{uiText("Support Requests")}</span>
              <span className="sm:hidden">{uiText("Support")}</span>
            </TabsTrigger>
            <TabsTrigger
              value="subscriptions"
              className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-600 data-[state=active]:to-emerald-700 data-[state=active]:text-white text-slate-300 p-2 sm:p-3 text-xs sm:text-sm"
            >
              <CreditCard className="h-4 w-4" />
              <span className="hidden sm:inline">{uiText("Subscriptions")}</span>
              <span className="sm:hidden">{uiText("Subs")}</span>
            </TabsTrigger>
            <TabsTrigger
              value="ai"
              className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-600 data-[state=active]:to-purple-700 data-[state=active]:text-white text-slate-300 p-2 sm:p-3 text-xs sm:text-sm col-span-2 sm:col-span-1"
            >
              <Crown className="h-4 w-4" />
              <span className="hidden sm:inline">{uiText("AI Agent")}</span>
              <span className="sm:hidden">{uiText("AI Agent")}</span>
            </TabsTrigger>
            {/* Removed Stripe Debug Tab Trigger */}
          </TabsList>

          <TabsContent value="dojos">
            <Card className="bg-slate-800/30 border-slate-700 backdrop-blur-sm">
              <CardHeader className="border-b border-slate-700 p-3 sm:p-6">
                <CardTitle className="text-slate-200 flex items-center gap-2 text-lg sm:text-xl">
                  <Database className="h-5 w-5 text-blue-400" />
                  {uiText("Dojo Management")}</CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-6">
                <DojoManagement />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="codes">
            <Card className="bg-slate-800/30 border-slate-700 backdrop-blur-sm">
              <CardHeader className="border-b border-slate-700 p-3 sm:p-6">
                <CardTitle className="text-slate-200 flex items-center gap-2 text-lg sm:text-xl">
                  <Settings className="h-5 w-5 text-green-400" />
                  {uiText("Admin Code Generator")}</CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-6">
                <AdminCodeGenerator />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="dojo-codes">
            <Card className="bg-slate-800/30 border-slate-700 backdrop-blur-sm">
              <CardHeader className="border-b border-slate-700 p-3 sm:p-6">
                <CardTitle className="text-slate-200 flex items-center gap-2 text-lg sm:text-xl">
                  <Key className="h-5 w-5 text-indigo-400" />
                  {uiText("Dojo Codes Management")}</CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-6">
                <DojoCodesManagement />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="support">
            <Card className="bg-slate-800/30 border-slate-700 backdrop-blur-sm">
              <CardHeader className="border-b border-slate-700 p-3 sm:p-6">
                <CardTitle className="text-slate-200 flex items-center gap-2 text-lg sm:text-xl">
                  <HeadphonesIcon className="h-5 w-5 text-orange-400" />
                  {uiText("Support Requests")}</CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-6">
                <SupportRequestsTab />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="subscriptions">
            <Card className="bg-slate-800/30 border-slate-700 backdrop-blur-sm">
              <CardHeader className="border-b border-slate-700 p-3 sm:p-6">
                <CardTitle className="text-slate-200 flex items-center gap-2 text-lg sm:text-xl">
                  <CreditCard className="h-5 w-5 text-emerald-400" />
                  {uiText("Subscription Management")}</CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-6">
                <SubscriptionManager />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="ai">
            <Card className="bg-slate-800/30 border-slate-700 backdrop-blur-sm">
              <CardHeader className="border-b border-slate-700 p-3 sm:p-6">
                <CardTitle className="text-slate-200 flex items-center gap-2 text-lg sm:text-xl">
                  <Crown className="h-5 w-5 text-purple-400" />
                  {uiText("AI Database Agent")}</CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-6">
                <AIAgent />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div></Layout>
  );
};

export default SuperUserDashboard;
